/**
 * useBlockSymbols — bridge external state (e.g. Zustand store selections)
 * into a StateManager's symbol paths via setState.
 *
 * Pairs with AppV2.onReady({ stateManager }) so host code can push derived
 * values into cogo JSON blocks' `#{local.X}` / `#{__app.X}` / arbitrary symbol
 * paths. Designed to be the production bridge for Phase 3 shell-as-cogo-app
 * scaffolds (see .planning/studio-shell-binding-strategy.md §3 items A + C).
 *
 * Semantics:
 * - Calls `sm.setState(path, value)` only when the value's identity changes
 *   since the previous render (Object.is comparison).
 * - Does NOT clear paths that were removed from the `overrides` object on a
 *   subsequent render — the consumer owns symbol lifecycle. If a path needs
 *   to reset, push an explicit reset value (e.g. the cogo JSON default) or
 *   call `sm.setState` directly.
 * - Safe when `sm` is null/undefined — the hook becomes a no-op, useful while
 *   AppV2.onReady has not yet fired on first mount.
 *
 * First-render flash: on first mount the effect runs AFTER paint, so the first
 * paint uses block.state.localState defaults. Subsequent paints reflect the
 * overrides. For scaffold migration this is acceptable; to eliminate the flash
 * the consumer should seed block.state.localState defaults that match the
 * initial store selection.
 */
import { useEffect, useRef } from "react"
import type { StateManager } from "@cogo/runtime-v2"

export function useBlockSymbols(
  sm: StateManager | null | undefined,
  overrides: Record<string, unknown>,
): void {
  const prevRef = useRef<Record<string, unknown>>({})

  useEffect(() => {
    if (!sm) return
    const prev = prevRef.current
    const next: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(overrides)) {
      next[key] = value
      if (!Object.is(prev[key], value)) {
        sm.setState(key, value)
      }
    }
    prevRef.current = next
  })
}
